import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Rating,
  Chip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  Star as StarIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, updateDoc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const LevelChoice = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState(null)

  const levels = [
    {
      id: 'A1',
      title: 'A1 - Başlangıç',
      description: 'Yeni Başlayanlar İçin • Temel Kelimeler',
      stars: 1,
      color: 'primary'
    },
    {
      id: 'A2',
      title: 'A2 - Temel Seviye',
      description: 'Günlük Konuşmalar • Basit İletişim',
      stars: 2,
      color: 'secondary'
    },
    {
      id: 'B1',
      title: 'B1 - Orta Seviye',
      description: 'Karmaşık Konular • Detaylı İletişim',
      stars: 3,
      color: 'success'
    },
    {
      id: 'B2',
      title: 'B2 - İleri Seviye',
      description: 'İleri Düzey Gramer • Profesyonel İletişim',
      stars: 4,
      color: 'warning'
    },
    {
      id: 'EXAM',
      title: 'SINAV',
      description: 'Dil Sınavlarına Hazırlık İçin Özel Sorular ve Kelimelerle Destekli',
      stars: 5,
      color: 'error'
    }
  ]

  const handleLevelSelect = async (level) => {
    setSelectedLevel(level)
    setLoading(true)

    try {
      // Kullanıcının seviyesini güncelle
      const userRef = doc(db, 'users', user.uid)
      await updateDoc(userRef, {
        level: level.id
      })

      // Yeni seviyede userProgress var mı kontrol et
      const progressRef = doc(db, 'levels', level.id, 'userProgress', user.uid)
      const progressDoc = await getDoc(progressRef)

      if (!progressDoc.exists()) {
        // Bu seviyede ilk kez - yeni progress başlat
        await initializeUserProgress(user.uid, level.id)
      }

      // Leaderboard kaydını kontrol et ve oluştur
      await setupLeaderboard(user.uid, level.id)

      toast.success(`${level.title} seviyesi başarıyla seçildi!`)
      
      // Sayfayı yenile
      window.location.reload()
    } catch (error) {
      console.error('Error changing level:', error)
      toast.error('Seviye değiştirilirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const initializeUserProgress = async (userId, level) => {
    try {
      const progressData = {
        userId: userId,
        level: level,
        completedWords: [],
        totalWords: 0,
        progress: 0,
        lastUpdated: new Date()
      }

      const progressRef = doc(db, 'levels', level, 'userProgress', userId)
      await updateDoc(progressRef, progressData)
    } catch (error) {
      console.error('Error initializing user progress:', error)
    }
  }

  const setupLeaderboard = async (userId, level) => {
    try {
      const leaderboardRef = doc(db, 'levels', level, 'leaderboard', userId)
      const leaderboardDoc = await getDoc(leaderboardRef)

      if (!leaderboardDoc.exists()) {
        // Leaderboard kaydı yoksa oluştur
        const initialScores = {
          username: userData?.username || user?.displayName || 'User',
          matchScore: 0,
          scrambleScore: 0,
          totalScore: 0
        }

        await updateDoc(leaderboardRef, initialScores)
      }
    } catch (error) {
      console.error('Error setting up leaderboard:', error)
    }
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Seviyenizi Seçin
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Current Level Info */}
        {userData?.level && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Mevcut seviyeniz: <strong>{userData.level}</strong>
            </Typography>
          </Alert>
        )}

        {/* Levels Grid */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {levels.map((level) => (
            <Card 
              key={level.id}
              sx={{ 
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: selectedLevel?.id === level.id ? `2px solid ${level.color}.main` : '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color={`${level.color}.main`}>
                      {level.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {level.description}
                    </Typography>
                  </Box>
                                     <Box sx={{ textAlign: 'center' }}>
                     <Rating 
                       value={level.stars} 
                       readOnly 
                       size="small"
                     />
                   </Box>
                </Box>

                <Button
                  variant="contained"
                  color={level.color}
                  fullWidth
                  size="large"
                  onClick={() => handleLevelSelect(level)}
                  disabled={loading || userData?.level === level.id}
                  startIcon={loading && selectedLevel?.id === level.id ? <CircularProgress size={20} /> : <SchoolIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    py: 1.5
                  }}
                >
                  {loading && selectedLevel?.id === level.id 
                    ? 'Seviye Değiştiriliyor...' 
                    : userData?.level === level.id 
                      ? 'Mevcut Seviye' 
                      : 'Bu Seviyeyi Seç'
                  }
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            Seviye değiştirdiğinizde, yeni seviyede ilerleme kaydınız başlatılacak ve leaderboard'a eklenileceksiniz.
          </Typography>
        </Alert>
      </Container>
    </SpaceTheme>
  )
}

export default LevelChoice
